import * as React from "react";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/overlays";
import { Field, Input, Textarea } from "@/components/ui/primitives";

/**
 * Confirmation for a consequential action.
 *
 * Destructive actions can require a reason (which is stored on the audit entry)
 * and, for the genuinely irreversible ones, typing a confirmation phrase. That
 * friction is deliberate: banning a user or removing every listing a breeder owns
 * should not be one stray click away.
 */
export const ConfirmDialog = ({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = "Confirm",
  variant = "default",
  requireReason,
  reasonLabel = "Reason",
  reasonHint,
  requirePhrase,
  extra,
  loading,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: React.ReactNode;
  confirmLabel?: string;
  variant?: "default" | "destructive" | "success";
  requireReason?: boolean;
  reasonLabel?: string;
  reasonHint?: string;
  /** When set, the admin must type this exactly to enable the confirm button. */
  requirePhrase?: string;
  extra?: React.ReactNode;
  loading?: boolean;
  /** Return value is ignored, so callers can use `target && mutate(...)` freely. */
  onConfirm: (reason: string) => unknown;
}) => {
  const [reason, setReason] = React.useState("");
  const [phrase, setPhrase] = React.useState("");

  React.useEffect(() => {
    if (open) {
      setReason("");
      setPhrase("");
    }
  }, [open]);

  const reasonOk = !requireReason || reason.trim().length >= 3;
  const phraseOk = !requirePhrase || phrase === requirePhrase;
  const canConfirm = reasonOk && phraseOk && !loading;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {variant === "destructive" && (
              <AlertTriangle className="h-4 w-4 text-destructive" aria-hidden />
            )}
            {title}
          </DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        <div className="space-y-4">
          {extra}

          {requireReason && (
            <Field
              label={reasonLabel}
              htmlFor="confirm-reason"
              required
              hint={reasonHint ?? "Recorded in the audit log."}
            >
              <Textarea
                id="confirm-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                autoFocus
                placeholder="Explain why, for the record"
              />
            </Field>
          )}

          {requirePhrase && (
            <Field
              label={`Type “${requirePhrase}” to confirm`}
              htmlFor="confirm-phrase"
              required
            >
              <Input
                id="confirm-phrase"
                value={phrase}
                onChange={(e) => setPhrase(e.target.value)}
                autoComplete="off"
                placeholder={requirePhrase}
              />
            </Field>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant={variant === "destructive" ? "destructive" : variant === "success" ? "success" : "default"}
            onClick={() => {
              void onConfirm(reason.trim());
            }}
            disabled={!canConfirm}
            loading={loading}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** Convenience hook for the open/target pattern these dialogs always need. */
export const useConfirm = <T,>() => {
  const [target, setTarget] = React.useState<T | null>(null);
  return {
    target,
    open: target !== null,
    ask: (value: T) => setTarget(value),
    close: () => setTarget(null),
    onOpenChange: (next: boolean) => {
      if (!next) setTarget(null);
    },
  };
};
